import type { AuthProviderType } from '../types/auth.types';
import { GATEWAY_BASE_URL } from '../config/gateway';

// --------------------------------------------------------------------------
// Auth config — provider-agnostic shell
// --------------------------------------------------------------------------

/**
 * Configuration consumed by individual auth providers.
 *
 * Azure-specific fields (authority, knownAuthorities) are only used by
 * AzureAuthProvider.  Other providers will ignore them.
 */
export interface AuthConfig {
  /** Which provider implementation to use */
  provider: AuthProviderType;

  /** OAuth client / application ID */
  clientId: string;

  /** OIDC authority URL (Azure: ciamlogin.com, Auth0: tenant.auth0.com, etc.) */
  authority: string;

  /** Where the IdP redirects after login */
  redirectUri: string;

  /** Where the IdP redirects after logout */
  postLogoutRedirectUri: string;

  /**
   * Scopes requested during interactive login.
   * For Azure External ID these are `openid profile email`.
   */
  loginScopes: string[];

  /**
   * Scopes requested when acquiring an **access token** for API calls.
   * For Azure this is the API scope like `api://<client-id>/access_as_user`.
   * If empty, loginScopes are used (results in an ID token — not recommended).
   */
  apiScopes: string[];

  /** Azure-specific: known authority hosts so MSAL trusts them */
  knownAuthorities: string[];

  /** Gateway base URL — used by the token revocation call on logout */
  gatewayUrl: string;
}

// --------------------------------------------------------------------------
// Build the config from env vars
// --------------------------------------------------------------------------

const providerFromEnv = (import.meta.env.VITE_AUTH_PROVIDER || 'azure') as AuthProviderType;

/**
 * Parse a space- or comma-separated scope string into an array.
 * Falls back to `defaultScopes` when the env var is empty.
 */
const parseScopes = (raw: string | undefined, defaultScopes: string[]): string[] => {
  if (!raw || !raw.trim()) return defaultScopes;
  return raw.split(/[\s,]+/).filter(Boolean);
};

const apiScopeEnv = import.meta.env.VITE_API_SCOPE as string | undefined;
const loginScopeEnv = import.meta.env.VITE_LOGIN_SCOPES as string | undefined;
const clientId = (import.meta.env.VITE_AZURE_CLIENT_ID ?? '') as string;

/**
 * Derive API scopes.
 *
 * CIAM External ID does not support self-referencing access tokens
 * (AADSTS90009). Instead we use the ID token as the bearer token for
 * gateway API calls.  The ID token has the correct `aud` (client ID),
 * `iss` (CIAM issuer), and `sub` (user OID) that the gateway needs.
 *
 * Because we always use login scopes, `acquireTokenSilent` returns a
 * refreshed ID token rather than requesting a custom API access token.
 */
const resolveApiScopes = (): string[] => {
  // Explicit env var (non-Azure providers or future overrides)
  if (apiScopeEnv && apiScopeEnv.trim()) {
    return parseScopes(apiScopeEnv, []);
  }
  if (loginScopeEnv && loginScopeEnv.trim()) {
    return parseScopes(loginScopeEnv, []);
  }
  return ['openid', 'profile', 'email'];
};

export const authConfig: AuthConfig = {
  provider: providerFromEnv,

  clientId,
  authority: import.meta.env.VITE_AZURE_AUTHORITY ?? '',
  redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI ?? '',
  postLogoutRedirectUri: import.meta.env.VITE_AZURE_POST_LOGOUT_URI ?? '',

  loginScopes: parseScopes(loginScopeEnv, ['openid', 'profile', 'email']),

  apiScopes: resolveApiScopes(),

  knownAuthorities: [
    `${import.meta.env.VITE_AZURE_SUBDOMAIN || 'koodookaCiamDev'}.ciamlogin.com`,
  ],

  gatewayUrl: GATEWAY_BASE_URL,
};

// --------------------------------------------------------------------------
// Validate required config (per-provider)
// --------------------------------------------------------------------------

const isProduction = import.meta.env.PROD;

const requiredByProvider: Record<AuthProviderType, (keyof AuthConfig)[]> = {
  azure: ['clientId', 'authority', 'redirectUri'],
  auth0: ['clientId', 'authority', 'redirectUri'],
  okta:  ['clientId', 'authority', 'redirectUri'],
  mock:  [],
};

const requiredKeys = requiredByProvider[authConfig.provider] ?? [];
const missingVars = requiredKeys.filter(
  (key) => !authConfig[key] || (typeof authConfig[key] === 'string' && !(authConfig[key] as string).trim()),
);

if (missingVars.length > 0) {
  const message = `[Auth Config] Missing required config for provider "${authConfig.provider}": ${missingVars.join(', ')}`;
  if (isProduction) {
    throw new Error(message);
  } else {
    console.warn(message);
  }
}
